import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaDna, FaFlask, FaCapsules, FaTruckMoving, FaDatabase, FaUserMd,
    FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import heroImage from '../../assets/download (4).png';

const PharmaPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaDna, title: 'R&D Digitization', desc: 'Accelerating drug discovery with AI-driven molecule analysis.', image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&q=80' },
        { icon: FaFlask, title: 'Lab Automation', desc: 'Integrating LIMS and robotic process automation in the lab.', image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80' },
        { icon: FaCapsules, title: 'Clinical Trials', desc: 'Decentralized trial platforms for better patient engagement.', image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&q=80' },
        { icon: FaDatabase, title: 'Real-World Evidence', desc: 'Big data analytics to demonstrate treatment value.', image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&q=80' },
        { icon: FaTruckMoving, title: 'Supply Chain', desc: 'Blockchain-enabled tracking for cold chain integrity.', image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80' },
        { icon: FaUserMd, title: 'HCP Engagement', desc: 'Omnichannel platforms for interacting with healthcare professionals.', image: 'https://images.unsplash.com/photo-1576091160550-21876ce7234f?w=800&q=80' },
    ];

    const faqs = [
        { q: 'How can AI speed up drug discovery?', a: 'AI models predict molecular interactions and toxicity, reducing the candidate pool and timeline by months.' },
        { q: 'What solutions do you offer for clinical trials?', a: 'We build eConsenting apps, patient diaries, and remote monitoring dashboards for decentralized trials.' },
        { q: 'Are your systems GxP compliant?', a: 'Yes, all our pharmaceutical solutions are validated according to GAMP 5 and 21 CFR Part 11 standards.' },
        { q: 'Can you integrate legacy LIMS?', a: 'We specialize in API-led integration to modernise legacy Laboratory Information Management Systems.' },
        { q: 'How do you secure patient data?', a: 'We employ advanced encryption and de-identification techniques to protect patient privacy in all datasets.' },
        { q: 'Do you support serialization?', a: 'Yes, our supply chain solutions support global serialization standards to prevent counterfeiting.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/60 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-4">Industries &gt; Life Sciences</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Pharma <span className="font-bold text-blue-400">4.0</span></h1>
                        <p className="text-lg font-light text-blue-100 leading-relaxed mb-8">
                            Data-driven innovation from molecule to market.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-blue-600 text-white font-medium rounded-sm hover:bg-blue-700 transition-all text-sm uppercase tracking-wide">
                                Explore Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6 text-slate-900">Next-Gen Therapeutics</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                The pharmaceutical industry is racing against time to deliver life-saving treatments. Tech-Inovant provides the digital infrastructure to accelerate every stage of the value chain.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                By breaking down data silos and leveraging AI, we help pharma companies bring safer, more effective drugs to patients faster.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">De-risked Discovery</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">Accelerated Trials</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">Compliant Manufacturing</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1585435557343-3b092031a831?w=800&q=80"
                                alt="Pharmaceutical Research"
                                className="rounded-sm shadow-sm"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">40%</div>
                                <div className="text-xs uppercase tracking-wider">Faster Time-to-Market</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4 text-slate-900">Digital Capabilities</h2>
                        <div className="w-20 h-1 bg-blue-600"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white border border-slate-200 hover:border-blue-400 transition-colors p-0 overflow-hidden group">
                                <div className="h-48 overflow-hidden relative">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-blue-600 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-blue-700 hover:text-blue-900 tracking-wide uppercase">
                                        View Details <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Metrics */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center text-slate-900">Proven Results</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Global Biotech', stat: '$50M', desc: 'Saved in trial costs via adaptive design.' },
                            { title: 'Top 10 Pharma', stat: '99.9%', desc: 'Audit readiness with automated compliance.' },
                            { title: 'CDMO Partner', stat: '3x', desc: 'Increase in batch release speed.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-blue-500">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-blue-800 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">Pharma FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Accelerate Discovery</h3>
                            <p className="text-slate-600 mb-8">Partner with us for digital transformation.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Dr. Jones" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Organization</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="BioTech Co." />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="jones@biotech.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Key Are</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors text-slate-700">
                                        <option>R&D / Discovery</option>
                                        <option>Clinical Operations</option>
                                        <option>Manufacturing</option>
                                        <option>Commercial</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-blue-600 text-white font-bold py-4 rounded-sm hover:bg-blue-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Discuss Requirements
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-12 bg-slate-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-light mb-6">Innovate for Life</h2>
                    <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                        Empowering the science that saves lives with cutting-edge technology.
                    </p>
                    <button className="px-8 py-3 bg-white text-slate-900 font-bold uppercase tracking-wide hover:bg-blue-50 transition-colors">
                        Partner With Us
                    </button>
                </div>
            </section>
        </div>
    );
};

export default PharmaPage;
